import { useMemo } from "react"

import { skuExtraDataColumns } from "~/components/planning/dynamicTableData/DynamicTableColumns"
import {
  useExtraDataAggregationMap,
  useExtraDataDisplayNameMap,
  useExtraDataFormattedKeys,
  useExtraDataMetadata,
  useExtraDataTypeMap,
} from "~/hooks/useSku"
import {
  ExtraDataTable,
  extraDataFieldNameFromColumnId,
  getExtraDataDisplayName,
  getExtraDataType,
} from "~/utils/dynamicTableUtils"
import type { FilterFieldType } from "~/utils/filter"
import { distinctValues } from "~/utils/tsUtils"
import type { ReplRow } from "./buildReplenishmentRow"
import { LINE_FILTER_FIELDS } from "./lineFilterFields"

const NO_FIELDS: string[] = []
const SKU_METADATA = { tableNames: [ExtraDataTable.SKU] }

/**
 * The line filter fields followed by the SKU extraData fields. String fields take their options
 * from ExtraFieldMetadata, so a field can be filtered on without the lines loading it.
 */
export function useLineExtraDataFields() {
  const { data: fields = NO_FIELDS } = useExtraDataFormattedKeys()
  const { data: typeMap } = useExtraDataTypeMap()
  const { data: displayNameMap } = useExtraDataDisplayNameMap()
  const { data: metadata } = useExtraDataMetadata(SKU_METADATA)
  return useMemo(() => {
    const valuesByKey = new Map(metadata?.map((row) => [row.fieldName, row.uniqueValues]))
    const filterFields: FilterFieldType[] = [...LINE_FILTER_FIELDS]
    const filterOptions: Record<string, string[]> = {}
    for (const field of fields) {
      const key = extraDataFieldNameFromColumnId(field)
      const type = getExtraDataType(key, ExtraDataTable.SKU, typeMap)
      const name = getExtraDataDisplayName(key, ExtraDataTable.SKU, displayNameMap)
      filterFields.push({ value: "DynamicFilterField", dynamicValue: field, name, type })
      if (type === "string") filterOptions[field] = distinctValues(valuesByKey.get(key) ?? [])
    }
    return { fields, typeMap, displayNameMap, filterFields, filterOptions }
  }, [fields, typeMap, displayNameMap, metadata])
}

/** The SKU extraData fields as filter fields and as line columns. */
export function useLineExtraData() {
  const extraData = useLineExtraDataFields()
  const { data: aggregationMap } = useExtraDataAggregationMap()
  return useMemo(
    () => ({
      ...extraData,
      columns: skuExtraDataColumns<ReplRow>(
        extraData.fields,
        extraData.displayNameMap,
        extraData.typeMap,
        aggregationMap,
      ),
    }),
    [extraData, aggregationMap],
  )
}
